import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Pagination from '../../components/base/Pagination';
import SegmentedControl from '../../components/base/SegmentedControl';
import ResultCard from './components/ResultCard';
import PropertyCard from './components/PropertyCard';
import PropertyFilters from './components/PropertyFilters';
import SearchFilterBar from './components/SearchFilterBar';
import MapView from './components/MapView';
import RegionMiniMap from './components/RegionMiniMap';
import { sortPropertiesByDate, type Property } from '../../mocks/properties';
import { formatAmount } from '../../utils/numberFormat';
import { pruneHiddenFilters } from '../../config/searchFilterFields';
import { filterByRegion, geometryToRegion, convexHull, allVertices, type DrawnRegion, type LatLng } from '../../utils/mapRegion';
import {
  fetchAreaBoundaries,
  fetchAreaInsights,
  type AreaBoundary,
  type AreaInsights,
  type LocationSuggestion,
} from '../../services/neighborhoodMapService';
import { useAuth } from '../../hooks/useAuth';
import { searchProperties, toFrontendProperty, trackSearchResultClick, type SearchProperty, type SearchSuggestions, type SearchAiInfo } from '../../services/ragSearchService';
import type { FilterState } from './types';
import {
  defaultFilters,
  createFiltersFromSearchParams,
  serializeFilters,
  areFiltersEqual,
} from './urlState';
import { buildSearchRequestBase } from './searchRequest';
import { countActiveFilters } from './filterActions';
import { useSeo } from '../../hooks/useSeo';
import { BrandRule } from '../../components/brand/BrandRule';
import { matchSaudiCity } from '../../config/saudiCities';

// Two ways to read the results (owner, 2026-10-07), switched in the header:
//   «عرض شامل» (whole view): compact cards in a dense grid, as many per page as fit;
//   «عرض البطاقات» (card view): one full card per property, every detail and photo.
// The map is the third view. 24 fills whole rows at 2, 3 and 4 columns.
type ListView = 'grid' | 'cards';
type ViewMode = ListView | 'map';
const PAGE_SIZE: Record<ListView, number> = { grid: 24, cards: 20 };
// The list view a visitor last chose, remembered in this browser only.
const LIST_VIEW_KEY = 'pv.search.listView';

function readStoredListView(): ListView | null {
  try {
    const value = window.localStorage.getItem(LIST_VIEW_KEY);
    return value === 'grid' || value === 'cards' ? value : null;
  } catch {
    return null;
  }
}
// Map view fetches the FULL matching set (not just the current page) so every
// result shows as a pin (clustered). Set high enough to cover the whole catalog —
// the map clusters thousands of pins fine, and a city filter returns far fewer.
const MAP_RESULTS_LIMIT = 10000;

// Shared looks for the page's small controls (THEME.md: outline pills; the
// chosen-location chip takes the decision-D3 selected recipe).
const OUTLINE_PILL =
  'inline-flex h-9 items-center gap-1.5 rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-3.5 text-[0.8125rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';
const LOCATION_CHIP =
  'inline-flex h-11 shrink-0 items-center gap-2 rounded-full border border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] pe-1.5 ps-4 text-[0.875rem] font-medium text-[var(--pv-color-primary)]';
const LOCATION_CHIP_CLEAR =
  'grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-[var(--pv-color-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';
const RESULTS_GRID = 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

/** Placeholder full cards for the card view while a page loads. */
function CardsSkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-6">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="h-[22rem] animate-pulse rounded-[16px] bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
      ))}
    </div>
  );
}

/** Placeholder cards while a page of results loads (the same grid, so nothing jumps). */
function ResultsSkeleton() {
  return (
    <ul role="list" aria-hidden="true" className={RESULTS_GRID}>
      {Array.from({ length: 8 }, (_, i) => (
        <li key={i} className="overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)]">
          <div className="aspect-[16/10] animate-pulse bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
          <div className="space-y-2.5 p-4">
            <div className="h-5 w-2/5 rounded bg-[var(--pv-color-surface-sunken)]" />
            <div className="h-4 w-4/5 rounded bg-[var(--pv-color-surface-sunken)]" />
            <div className="h-4 w-3/5 rounded bg-[var(--pv-color-surface-sunken)]" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// Haversine formula to calculate distance between two coordinates
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}


// The /api/search request builder lives in ./searchRequest (unit-tested there);
// it prunes values the filters panel hides before anything is sent.

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('search');
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  useSeo({
    title: lang === 'ar' ? 'بحث العقارات | صفقات 24' : 'Property search | Safaqat 24',
    description: lang === 'ar'
      ? 'ابحث وصفِّ العقارات للبيع والإيجار في السعودية حسب المدينة والحي والنوع والسعر.'
      : 'Search and filter properties for sale and rent across Saudi Arabia by city, district, type, and price.',
  });

  const { session } = useAuth();

  // The list view (whole / cards) and the map are kept apart, so leaving the map
  // returns to whichever list view was in use. ?view=cards|map; whole view has
  // no param; without one the visitor's last choice applies.
  const [listView, setListView] = useState<ListView>(() => {
    const view = searchParams.get('view');
    return view === 'cards' ? 'cards' : readStoredListView() ?? 'grid';
  });
  const [showMap, setShowMap] = useState(() => searchParams.get('view') === 'map');
  const viewMode: ViewMode = showMap ? 'map' : listView;
  const pageSize = PAGE_SIZE[listView];
  // Set by the "Draw your region on the map" shortcut (?draw=polygon); arms the
  // polygon tool inside MapView, then resets once consumed.
  const [autoDrawPolygon, setAutoDrawPolygon] = useState(false);
  // The area drawn on the map (radius/polygon). Owned here so it survives the
  // grid↔map toggle and so grid view can filter by it + preview it on a mini-map.
  const [drawnRegion, setDrawnRegion] = useState<DrawnRegion>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'date-newest' | 'date-oldest'>(() => {
    const s = searchParams.get('sort');
    return s === 'price-asc' || s === 'price-desc' || s === 'date-newest' || s === 'date-oldest'
      ? s
      : 'date-newest';
  });
  const [isLoading, setIsLoading] = useState(true);
  // The «كل الفلاتر» drawer: a bottom sheet on phones, a side panel from lg. The
  // filter bar holds the everyday filters, so it starts closed at every size.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);

  // While the drawer is open: lock the page scroll, close on Escape, move focus
  // into it, and hand focus back to whatever opened it.
  useEffect(() => {
    if (!filtersExpanded || typeof document === 'undefined') return;
    const opener = document.activeElement as HTMLElement | null;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFiltersExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    const raf = window.requestAnimationFrame(() => drawerCloseRef.current?.focus());
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
      window.cancelAnimationFrame(raf);
      opener?.focus?.();
    };
  }, [filtersExpanded]);
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });
  const [totalPages, setTotalPages] = useState(1);
  const resultsTopRef = useRef<HTMLDivElement>(null);

  // Search state
  const [ragResults, setRagResults] = useState<Property[]>([]);
  const [ragTotal, setRagTotal] = useState<number>(0);
  const [searchMethod, setSearchMethod] = useState<'vector' | 'hybrid' | 'database' | 'local'>('local');
  const [aiInfo, setAiInfo] = useState<SearchAiInfo | null>(null);
  const [filterDescription, setFilterDescription] = useState<string>('');
  const [didYouMean, setDidYouMean] = useState<{ original: string; corrected: string } | null>(null);
  const [ragError, setRagError] = useState<string | null>(null);
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestions | null>(null);

  // Full matching set for the map (and the grid's drawn-region filter). Fetched
  // separately from the paginated grid results so the map plots EVERY result,
  // not just the current page of 20.
  const [mapProperties, setMapProperties] = useState<Property[]>([]);
  const [mapLoading, setMapLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // "Draw your region" shortcut: ?draw=polygon forces map view + arms the
  // polygon tool, then we strip the param so refresh/back doesn't re-trigger it.
  useEffect(() => {
    if (searchParams.get('draw') !== 'polygon') return;
    setShowMap(true);
    setAutoDrawPolygon(true);
    const next = new URLSearchParams(searchParams);
    next.delete('draw');
    // Treat this strip as an internal URL write so the URL→state sync below
    // doesn't read the (still view-less) URL and revert us back to grid.
    isSyncingToUrl.current = true;
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  // Hidden values (e.g. bedrooms on a ?type=LAND deep link) are dropped on the way
  // in, so state, chips and URL only ever hold what the filters panel shows.
  const [filters, setFilters] = useState<FilterState>(() =>
    pruneHiddenFilters(createFiltersFromSearchParams(searchParams)),
  );
  const isSyncingToUrl = useRef(false);
  // Latest URL query string, tracked in a ref so the filters→URL writer effect
  // can compare against it WITHOUT depending on `searchParams`/`setSearchParams`.
  // In react-router v6 `setSearchParams`'s identity changes on every URL change,
  // so depending on it makes the writer loop forever (write → URL change → new
  // setSearchParams identity → write …), pegging the CPU and bouncing card
  // clicks back to /search. The ref breaks that cycle.
  const currentParamsRef = useRef('');
  currentParamsRef.current = searchParams.toString();

  // Selected district (from search or a ?areaId= deep link): its boundary frames
  // the map and its insights show. The listing filter itself is server-side via
  // filters.areaId - this state is only the visual + chip metadata.
  const [selectedAreaBoundary, setSelectedAreaBoundary] = useState<AreaBoundary | null>(null);
  const [selectedAreaInsights, setSelectedAreaInsights] = useState<AreaInsights | null>(null);
  const [selectedAreaLoading, setSelectedAreaLoading] = useState(false);
  // Outline (convex hull) of a searched CITY's districts - drawn as a border and
  // framed on the mini-map. Null unless a city (not a district) is scoped.
  const [cityRegion, setCityRegion] = useState<DrawnRegion>(null);
  // The district picked in the location search, so the title and chip can name
  // it even while the boundary / insights lookups are off (VITE_DISTRICT_*).
  const [pickedArea, setPickedArea] = useState<{ id: string; nameAr: string; nameEn: string } | null>(null);

  // Debounced filters for API calls - prevents rapid API requests.
  // Initialized from the SAME reference as `filters` so the first debounce
  // tick (300ms after mount) doesn't trip a redundant state update and fire
  // a second /api/search call against the per-IP rate limit.
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>(filters);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);

    // Cleanup on unmount or filter change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters]);

  // Reset to page 1 whenever filters, sort, or the drawn region change — the
  // current page number no longer makes sense against a different result set.
  // Skip the very first run so a deep-linked ?page=N survives mount.
  const didMountPageReset = useRef(false);
  useEffect(() => {
    if (!didMountPageReset.current) {
      didMountPageReset.current = true;
      return;
    }
    setCurrentPage(1);
  }, [filters, sortBy, drawnRegion]);

  // RAG Search API call (server-paginated grid). Skipped while a region is drawn
  // — the grid then paginates the in-region set client-side (see gridProperties),
  // so a server page fetch is unneeded and would only flash the loader.
  useEffect(() => {
    // With a region drawn the grid pages the in-region set; nothing to fetch here,
    // and a filter change's startLoading() must not leave the grid loading.
    if (drawnRegion) {
      setIsLoading(false);
      return;
    }
    const performRAGSearch = async () => {
      setIsLoading(true);
      setRagError(null);

      try {
        // Whether the user typed a query — drives the filter-description copy below.
        const hasTextQuery = !!(debouncedFilters.query && debouncedFilters.query.trim());

        const response = await searchProperties({
          ...buildSearchRequestBase(debouncedFilters, sortBy, lang as 'en' | 'ar'),
          page: currentPage,
          limit: pageSize,
          // Send the auth token so subscribers get their AI tier (rerank / LLM
          // understanding). Anonymous users still get free core semantic search.
          token: session?.token ?? null,
        });

        if (response.success && response.data) {
          const convertedResults = response.data.properties.map((p: SearchProperty) => {
            const converted = toFrontendProperty(p);
            return converted as Property;
          });
          setRagResults(convertedResults);
          setRagTotal(response.data.pagination?.total ?? convertedResults.length);
          setTotalPages(Math.max(1, response.data.pagination?.totalPages ?? 1));
          setSearchMethod(response.data.meta?.searchMethod ?? 'database');
          setAiInfo(response.data.ai ?? null);
          setFilterDescription(hasTextQuery ? (response.data.filters?.description ?? '') : '');
          setDidYouMean(response.data.meta?.didYouMean ?? null);
          // Capture suggestions when results are empty
          const suggestions = response.data.suggestions ?? response.suggestions ?? null;
          setSearchSuggestions(convertedResults.length === 0 ? suggestions : null);
        } else {
          setRagError(response.error || 'Search failed');
          setRagResults([]);
          setRagTotal(0);
          setTotalPages(1);
          setSearchMethod('local');
          setAiInfo(null);
          setFilterDescription('');
          setDidYouMean(null);
          setSearchSuggestions(null);
        }
      } catch (error) {
        console.error('RAG search error:', error);
        setRagError('Failed to connect to search service');
        setRagResults([]);
        setRagTotal(0);
        setSearchMethod('local');
      } finally {
        setIsLoading(false);
      }
    };

    performRAGSearch();
  }, [debouncedFilters, sortBy, lang, currentPage, pageSize, session?.token, drawnRegion]);

  // Map data fetch — the FULL matching set, independent of pagination, so the
  // map plots every result (not just the page's 20) and the drawn-region filter
  // narrows the whole set. AI is off here for deterministic completeness (we want
  // ALL filter matches, not a semantic top-K). Runs only when the map is open or
  // a region is drawn, to avoid the extra call during plain grid browsing.
  const shouldFetchMap = viewMode === 'map' || drawnRegion !== null;
  useEffect(() => {
    if (!shouldFetchMap) return;
    let cancelled = false;
    const run = async () => {
      setMapLoading(true);
      try {
        const response = await searchProperties({
          ...buildSearchRequestBase(debouncedFilters, sortBy, lang as 'en' | 'ar'),
          ai: false,
          page: 1,
          limit: MAP_RESULTS_LIMIT,
          token: session?.token ?? null,
        });
        if (cancelled) return;
        if (response.success && response.data) {
          setMapProperties(response.data.properties.map((p: SearchProperty) => toFrontendProperty(p) as Property));
        } else {
          setMapProperties([]);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Map search error:', error);
          setMapProperties([]);
        }
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [shouldFetchMap, debouncedFilters, sortBy, lang, session?.token]);

  // Calculate distance from landmark for each property
  const propertiesWithDistance = useMemo(() => {
    if (!filters.landmark) return null;

    const { lat, lng } = filters.landmark;
    const radiusKm = filters.radiusKm || 5;

    return (props: Property[]) => props
      .map((property) => ({
        ...property,
        distanceFromLandmark: calculateDistance(lat, lng, property.latitude, property.longitude),
      }))
      .filter((property) => property.distanceFromLandmark <= radiusKm)
      .sort((a, b) => a.distanceFromLandmark - b.distanceFromLandmark);
  }, [filters.landmark, filters.radiusKm]);

  const filteredAndSortedProperties = useMemo(() => {
    // RAG search is the primary source of truth
    let filtered = [...ragResults];

    // Apply date-oldest sorting locally since backend doesn't support it
    if (sortBy === 'date-oldest') {
      filtered = sortPropertiesByDate(filtered, false);
    }

    // Apply landmark filter
    if (propertiesWithDistance) {
      filtered = propertiesWithDistance(filtered);
    }

    return filtered;
  }, [sortBy, ragResults, propertiesWithDistance]);

  // The full matching set with the same local transforms the grid applies
  // (local date-oldest sort + landmark radius). This is what the map plots and
  // what the drawn-region filter narrows — so the map shows ALL matching
  // results, never a single page.
  const mapFilteredProperties = useMemo(() => {
    let arr = [...mapProperties];
    if (sortBy === 'date-oldest') arr = sortPropertiesByDate(arr, false);
    if (propertiesWithDistance) arr = propertiesWithDistance(arr);
    return arr;
  }, [mapProperties, sortBy, propertiesWithDistance]);

  // Full set of matches inside the drawn region (null when no region is drawn).
  const regionResults = useMemo(
    () => (drawnRegion ? filterByRegion(mapFilteredProperties, drawnRegion) : null),
    [drawnRegion, mapFilteredProperties],
  );

  // Pages for the list: server-driven normally; client-side over the in-region
  // set when a region is drawn — so a page always holds one page size.
  const effectiveTotalPages = useMemo(
    () => (regionResults ? Math.max(1, Math.ceil(regionResults.length / pageSize)) : totalPages),
    [regionResults, totalPages, pageSize],
  );

  // The current page of list results. The server returns the right page when no
  // region is drawn; with a region we slice the in-region set client-side so the
  // whole set is never dumped onto one page.
  const gridProperties = useMemo(() => {
    if (!regionResults) return filteredAndSortedProperties;
    const start = (currentPage - 1) * pageSize;
    return regionResults.slice(start, start + pageSize);
  }, [regionResults, filteredAndSortedProperties, currentPage, pageSize]);

  // Exact outline of a searched DISTRICT for the mini-map preview. Hidden from
  // external reviewers until launch (VITE_DISTRICT_BOUNDARY_ENABLED) — null keeps
  // the district outline off the grid mini-map too.
  const districtRegion = useMemo(
    () =>
      import.meta.env?.VITE_DISTRICT_BOUNDARY_ENABLED === 'true' && filters.areaId && selectedAreaBoundary
        ? geometryToRegion(selectedAreaBoundary.boundary)
        : null,
    [filters.areaId, selectedAreaBoundary],
  );

  // What the grid-view mini-map should show, in priority order: a hand-drawn
  // region → a searched district (its border) → a searched city (framed, no
  // border). Null for an all-out search (no map).
  const miniMapPreview = useMemo(() => {
    if (drawnRegion) {
      if ((regionResults?.length ?? 0) === 0) return null;
      return { kind: 'drawn' as const, region: drawnRegion, frameBounds: undefined as LatLng[] | undefined, properties: regionResults ?? [], name: '' };
    }
    if (districtRegion) {
      return { kind: 'district' as const, region: districtRegion, frameBounds: undefined as LatLng[] | undefined, properties: filteredAndSortedProperties, name: (lang === 'ar' ? selectedAreaBoundary?.nameAr : selectedAreaBoundary?.nameEn) ?? '' };
    }
    if (filters.cityId && cityRegion) {
      return { kind: 'city' as const, region: cityRegion, frameBounds: undefined as LatLng[] | undefined, properties: filteredAndSortedProperties, name: filters.cityName ?? '' };
    }
    return null;
  }, [drawnRegion, regionResults, districtRegion, filters.cityId, filters.cityName, cityRegion, filteredAndSortedProperties, selectedAreaBoundary, lang]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll the results region into view so the next page is visible without
    // forcing the user to scroll back up. Falls back to top-of-page if the
    // ref isn't mounted yet for any reason.
    if (resultsTopRef.current) {
      resultsTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const handleFilterChange = useCallback(
    (changes: Partial<FilterState>) => {
      setFilters((previous) => {
        // A change can hide fields (villa with 3 bedrooms → أرض hides bedrooms;
        // للبيع hides pet-friendly): blank them so they are never sent.
        const next = pruneHiddenFilters({ ...previous, ...changes });
        return next;
      });
      startLoading();
    },
    [startLoading],
  );

  const clearAllFilters = useCallback(() => {
    setFilters(defaultFilters);
    startLoading();
  }, [startLoading]);

  // A city/district was picked from the LocationSearch autocomplete. Resolve it
  // to the exact, deterministic filter: a CITY sets cityId (+ display name); a
  // DISTRICT sets areaId. Selecting one clears the other and resets any radius.
  const handleLocationSelect = useCallback(
    (loc: LocationSuggestion) => {
      if (loc.kind === 'city') {
        handleFilterChange({
          cityId: loc.id,
          cityName: lang === 'ar' ? (loc.nameAr || loc.nameEn) : loc.nameEn,
          areaId: null,
          locationLat: loc.latitude ?? null,
          locationLng: loc.longitude ?? null,
          locationRadiusKm: null,
        });
      } else {
        setPickedArea({ id: loc.id, nameAr: loc.nameAr || loc.nameEn, nameEn: loc.nameEn });
        handleFilterChange({
          areaId: loc.id,
          cityId: null,
          cityName: null,
          locationLat: loc.latitude ?? null,
          locationLng: loc.longitude ?? null,
          locationRadiusKm: null,
        });
      }
    },
    [handleFilterChange, lang],
  );

  // Sync filters + view/sort/page → URL. Built on top of serializeFilters so
  // the existing filter params are preserved; view/sort/page are appended only
  // when they differ from their defaults to keep clean URLs.
  useEffect(() => {
    const params = serializeFilters(filters);
    if (viewMode !== 'grid') params.set('view', viewMode);
    if (sortBy !== 'date-newest') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', String(currentPage));
    // Only write when the URL would actually change. This guard + omitting
    // setSearchParams from the deps prevents the write→change→write loop
    // (see currentParamsRef above).
    if (params.toString() === currentParamsRef.current) return;
    isSyncingToUrl.current = true;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, viewMode, sortBy, currentPage]);

  // Sync URL → filters + view/sort/page (browser back/forward navigation)
  useEffect(() => {
    if (isSyncingToUrl.current) {
      isSyncingToUrl.current = false;
      return;
    }
    const nextFilters = pruneHiddenFilters(createFiltersFromSearchParams(searchParams));
    setFilters((current) =>
      areFiltersEqual(current, nextFilters) ? current : nextFilters
    );
    const view = searchParams.get('view');
    const nextShowMap = view === 'map';
    setShowMap((current) => (current === nextShowMap ? current : nextShowMap));
    if (!nextShowMap) {
      const nextList: ListView = view === 'cards' ? 'cards' : readStoredListView() ?? 'grid';
      setListView((current) => (current === nextList ? current : nextList));
    }
    const s = searchParams.get('sort');
    const nextSort: typeof sortBy =
      s === 'price-asc' || s === 'price-desc' || s === 'date-newest' || s === 'date-oldest'
        ? s
        : 'date-newest';
    setSortBy((current) => (current === nextSort ? current : nextSort));
    const p = parseInt(searchParams.get('page') ?? '', 10);
    const nextPage = Number.isFinite(p) && p > 0 ? p : 1;
    setCurrentPage((current) => (current === nextPage ? current : nextPage));
  }, [searchParams]);

  // Resolve the selected district's outline + insights when areaId changes.
  useEffect(() => {
    const areaId = filters.areaId;
    if (!areaId) {
      setSelectedAreaBoundary(null);
      setSelectedAreaInsights(null);
      setSelectedAreaLoading(false);
      return;
    }
    // Both district features are hidden from external reviewers until launch
    // (VITE_DISTRICT_* flags). When both are off, don't fetch boundary/insights
    // at all — so there's no network trace of the feature to a curious reviewer.
    if (
      import.meta.env?.VITE_DISTRICT_BOUNDARY_ENABLED !== 'true' &&
      import.meta.env?.VITE_DISTRICT_INSIGHTS_ENABLED !== 'true'
    ) {
      setSelectedAreaBoundary(null);
      setSelectedAreaInsights(null);
      setSelectedAreaLoading(false);
      return;
    }
    let cancelled = false;
    setSelectedAreaLoading(true);
    Promise.all([
      fetchAreaBoundaries().then((all) => all.find((a) => a.areaId === areaId) ?? null).catch(() => null),
      fetchAreaInsights(areaId).catch(() => null),
    ])
      .then(([boundary, insights]) => {
        if (cancelled) return;
        setSelectedAreaBoundary(boundary);
        setSelectedAreaInsights(insights);
      })
      .finally(() => {
        if (!cancelled) setSelectedAreaLoading(false);
      });
    return () => { cancelled = true; };
  }, [filters.areaId]);

  // Outline the searched CITY on the mini-map: convex hull of its district
  // boundaries → a single border polygon. Only for a city scope (no district).
  useEffect(() => {
    const cityId = filters.cityId;
    if (!cityId || filters.areaId) { setCityRegion(null); return; }
    let cancelled = false;
    fetchAreaBoundaries(cityId)
      .then((areas) => {
        if (cancelled) return;
        const hull = convexHull(allVertices(areas.map((a) => a.boundary)));
        setCityRegion(hull.length >= 3 ? { kind: 'polygon', path: hull } : null);
      })
      .catch(() => { if (!cancelled) setCityRegion(null); });
    return () => { cancelled = true; };
  }, [filters.cityId, filters.areaId]);

  const handleSortChange = (value: typeof sortBy) => {
    setSortBy(value);
    startLoading();
  };

  // The results order lives in the filters panel (sidebar and phone sheet), first,
  // where people look for it (owner, 2026-10); it used to sit in the results toolbar.
  const sortControl = {
    label: t('search.results.sortLabel'),
    value: sortBy,
    onChange: (value: string) => handleSortChange(value as typeof sortBy),
    options: [
      { value: 'date-newest', label: t('search.results.sortOptions.newest'), icon: <i className="ri-time-line" /> },
      { value: 'date-oldest', label: t('search.results.sortOptions.oldest'), icon: <i className="ri-history-line" /> },
      { value: 'price-asc', label: t('search.results.sortOptions.priceLow'), icon: <i className="ri-arrow-up-line" /> },
      { value: 'price-desc', label: t('search.results.sortOptions.priceHigh'), icon: <i className="ri-arrow-down-line" /> },
    ],
  };

  const handlePropertySelect = useCallback(
    (property: Property) => {
      setSelectedProperty(property);
      // Record the search-result click-through (fire-and-forget; never blocks nav).
      trackSearchResultClick(property.id, undefined, session?.token ?? null);
      // Navigate to property page in both grid and map view
      navigate(`/property/${property.id}`);
    },
    [navigate, session?.token],
  );

  // The header count must reflect the TOTAL matching listings, not the current
  // page slice (≤20). `ragTotal` is the server total; when an area is drawn the
  // relevant total is the in-region count.
  const totalCount = drawnRegion ? (regionResults?.length ?? 0) : ragTotal;
  // «2,510 عقار» / «3 عقارات» / «عقار واحد»: the count, shown once, in the header.
  const countText = t('search.results.total', { count: totalCount, n: formatAmount(totalCount) });

  // The chosen district's name: its boundary or insights record when those are
  // on, else the name picked in the location search. Null for a city or no place.
  const districtName = useMemo(() => {
    if (!filters.areaId) return null;
    const fromLookup = lang === 'ar'
      ? selectedAreaBoundary?.nameAr ?? selectedAreaInsights?.nameAr
      : selectedAreaBoundary?.nameEn ?? selectedAreaInsights?.nameEn;
    const fromPick = pickedArea?.id === filters.areaId ? (lang === 'ar' ? pickedArea.nameAr : pickedArea.nameEn) : null;
    return fromLookup || fromPick || null;
  }, [filters.areaId, lang, selectedAreaBoundary, selectedAreaInsights, pickedArea]);

  // The city in the page's language: a link can carry it in English
  // (?city=Jeddah from a property's breadcrumb) and the title said «في Jeddah».
  const cityTitle = useMemo(() => {
    if (!filters.cityName) return '';
    const match = matchSaudiCity(filters.cityName);
    return match ? (lang === 'ar' ? match.nameAr : match.nameEn) : filters.cityName;
  }, [filters.cityName, lang]);

  // One page title built from the filters: «عقارات للإيجار في جدة».
  const pageTitle = t('search.results.titleIn', {
    title:
      filters.listingType === 'rent'
        ? t('search.results.titleRent')
        : filters.listingType === 'buy'
          ? t('search.results.titleBuy')
          : t('search.results.titleAll'),
    place: districtName || cityTitle || t('search.results.placeDefault'),
  });
  const activeFilterCount = countActiveFilters(filters, defaultFilters);
  const query = filters.query.trim();

  // Screen-reader announcement for async search status: error, "did you mean",
  // or the result count once results settle. Read by the polite live region.
  const searchAnnouncement = useMemo(() => {
    if (isLoading) return '';
    if (ragError) {
      return lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not reach the server';
    }
    const parts: string[] = [];
    if (didYouMean) {
      parts.push(
        lang === 'ar'
          ? `نعرض نتائج لـ ${didYouMean.corrected}`
          : `Showing results for ${didYouMean.corrected}`,
      );
    }
    parts.push(countText);
    return parts.join('. ');
  }, [isLoading, ragError, didYouMean, countText, lang]);

  // The chosen city / district (and the district radius), shown in the filter bar
  // beside the location search they came from.
  const locationChips = (
    <>
      {filters.cityName && (
        <span className={LOCATION_CHIP}>
          <i className="ri-building-2-line text-base leading-none" aria-hidden="true" />
          {t('search.city.chip', { name: cityTitle || filters.cityName, defaultValue: 'City: {{name}}' })}
          <button
            type="button"
            onClick={() => handleFilterChange({ cityId: null, cityName: null, locationLat: null, locationLng: null })}
            className={LOCATION_CHIP_CLEAR}
            aria-label={t('search.city.clear', { defaultValue: 'Clear city' })}
          >
            <i className="ri-close-line text-base leading-none" aria-hidden="true" />
          </button>
        </span>
      )}
      {filters.areaId && (
        <span className={LOCATION_CHIP}>
          <i className="ri-map-pin-2-line text-base leading-none" aria-hidden="true" />
          {t('search.district.chip', { name: districtName ?? '…', defaultValue: 'District: {{name}}' })}
          <button
            type="button"
            onClick={() => handleFilterChange({ areaId: null, locationLat: null, locationLng: null, locationRadiusKm: null })}
            className={LOCATION_CHIP_CLEAR}
            aria-label={t('search.district.clear', { defaultValue: 'Clear district' })}
          >
            <i className="ri-close-line text-base leading-none" aria-hidden="true" />
          </button>
        </span>
      )}
      {/* "Within N km": only for a chosen DISTRICT with a centroid. */}
      {filters.areaId && filters.locationLat != null && filters.locationLng != null && (
        <label className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] px-4 text-[0.875rem] text-[var(--pv-color-text)]">
          <i className="ri-focus-3-line text-base leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
          <span className="sr-only">{t('search.radius.label', { defaultValue: 'Search radius' })}</span>
          <select
            value={filters.locationRadiusKm ?? ''}
            onChange={(e) => handleFilterChange({ locationRadiusKm: e.target.value ? Number(e.target.value) : null })}
            className="bg-transparent font-medium focus:outline-none"
            aria-label={t('search.radius.label', { defaultValue: 'Search radius' })}
          >
            <option value="">{t('search.radius.exact', { defaultValue: 'This area only' })}</option>
            {[1, 3, 5, 10].map((km) => (
              <option key={km} value={km}>
                {t('search.radius.within', { km, defaultValue: `+ ${km} km` })}
              </option>
            ))}
          </select>
        </label>
      )}
    </>
  );

  const selectView = (next: ViewMode) => {
    if (next === 'map') {
      setShowMap(true);
      return;
    }
    setShowMap(false);
    if (next !== listView) {
      setListView(next);
      setCurrentPage(1);
      try {
        window.localStorage.setItem(LIST_VIEW_KEY, next);
      } catch {
        /* storage unavailable: the URL still carries the view */
      }
    }
  };

  const retrySearch = () => {
    setRagError(null);
    setDebouncedFilters({ ...debouncedFilters });
  };

  // List views: the page of results. Map: the full set it plots.
  const resultsLoading = viewMode === 'map' ? mapLoading && mapFilteredProperties.length === 0 : isLoading;
  const isAiSearch = searchMethod === 'vector' || searchMethod === 'hybrid';

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]">
      <Navigation />

      <main className="relative">
        {/* Polite live region announcing async search status (count / "did you
            mean" / error) to assistive tech without a visual change. */}
        <div className="sr-only" aria-live="polite" role="status">
          {searchAnnouncement}
        </div>

        {/* The filter bar (wasalt.sa pattern): location, then one pill per everyday
            filter; «كل الفلاتر» opens the drawer at the end of the page. */}
        <SearchFilterBar
          filters={filters}
          onFiltersChange={handleFilterChange}
          onClearAll={clearAllFilters}
          onLocationSelect={handleLocationSelect}
          locationChips={locationChips}
          sort={sortControl}
          onOpenAllFilters={() => setFiltersExpanded(true)}
        />

        <div className="mx-auto max-w-[1600px] px-3 pb-16 pt-6 sm:px-4">
          {/* One title built from the filters, and the count once. */}
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <div className="min-w-0">
              <h1 className="text-[1.375rem] font-semibold leading-tight text-[var(--pv-color-primary)] sm:text-[1.625rem]">
                {pageTitle}
              </h1>
              <BrandRule className="mt-3" />
              <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                {isLoading && ragTotal === 0 && !drawnRegion ? (
                  <span
                    className="inline-block h-4 w-24 animate-pulse rounded bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                ) : (
                  <span className={`tabular-nums transition-opacity ${isLoading && !drawnRegion ? 'opacity-50' : ''}`}>
                    {countText}
                  </span>
                )}
                {query && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="min-w-0 truncate">{lang === 'ar' ? `«${query}»` : `“${query}”`}</span>
                  </>
                )}
                {isAiSearch && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span
                      className="inline-flex items-center gap-1 text-[var(--pv-color-primary)]"
                      title={aiInfo?.reranked
                        ? (lang === 'ar' ? 'نتائج مرتّبة بالذكاء الاصطناعي حسب مطابقتها لطلبك' : 'Results AI-ranked by how well they match your request')
                        : (lang === 'ar' ? 'بحث ذكي يفهم المعنى وليس الكلمات فقط' : 'Smart search that understands meaning, not just keywords')}
                    >
                      <i className="ri-sparkling-2-line text-base leading-none" aria-hidden="true" />
                      {aiInfo?.reranked
                        ? t('search.ai.badgeReranked', { defaultValue: lang === 'ar' ? 'ترتيب ذكي' : 'AI-ranked' })
                        : t('search.ai.badgeVector', { defaultValue: lang === 'ar' ? 'بحث ذكي' : 'AI Search' })}
                    </span>
                  </>
                )}
              </p>
            </div>
            {/* The view switch. Its labels say what each view is (owner, 2026-10-07:
                no small caption under it). */}
            <div className="flex flex-col items-start sm:items-end">
              <div className="hidden sm:block">
                <SegmentedControl<ViewMode>
                  value={viewMode}
                  onChange={selectView}
                  options={[
                    { value: 'grid', label: t('search.results.viewModes.grid'), icon: 'ri-layout-grid-line' },
                    { value: 'cards', label: t('search.results.viewModes.cards'), icon: 'ri-layout-row-line' },
                    { value: 'map', label: t('search.results.viewModes.map'), icon: 'ri-map-2-line' },
                  ]}
                  ariaLabel={t('search.results.viewModes.label')}
                  size="sm"
                />
              </div>
              {/* Phones: the two list views in short; the map is on the floating bar. */}
              <div className="sm:hidden">
                <SegmentedControl<ListView>
                  value={listView}
                  onChange={selectView}
                  options={[
                    { value: 'grid', label: t('search.results.viewModes.gridShort'), icon: 'ri-layout-grid-line' },
                    { value: 'cards', label: t('search.results.viewModes.cardsShort'), icon: 'ri-layout-row-line' },
                  ]}
                  ariaLabel={t('search.results.viewModes.label')}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* "Did you mean": the corrected query, with a way back to the exact one. */}
          {didYouMean && (
            <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
              <i className="ri-magic-line text-base leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
              <span>
                {t('search.ai.showingResultsFor', { defaultValue: lang === 'ar' ? 'نعرض نتائج لـ' : 'Showing results for' })}{' '}
                <span className="font-semibold text-[var(--pv-color-text)]">{didYouMean.corrected}</span>
              </span>
              <button
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, query: `"${didYouMean.original}"` }))}
                className="font-medium text-[var(--pv-color-primary)] underline underline-offset-4"
              >
                {t('search.ai.searchInstead', { defaultValue: lang === 'ar' ? `ابحث بدلاً من ذلك عن «${didYouMean.original}»` : `Search instead for “${didYouMean.original}”` })}
              </button>
            </p>
          )}

          {/* What the search understood from a sentence (the backend already
              computes filters.description, so this costs nothing). */}
          {filterDescription && isAiSearch && (
            <p className="mt-3 flex items-start gap-2 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">
              <i className="ri-sparkling-2-line mt-1 text-base leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
              <span>
                <span className="font-semibold text-[var(--pv-color-text)]">
                  {t('search.ai.understood', { defaultValue: lang === 'ar' ? 'فهمنا طلبك:' : 'We understood:' })}
                </span>{' '}
                {filterDescription}
              </span>
            </p>
          )}

          {/* The results. Also the anchor a page change scrolls back to. */}
          <div ref={resultsTopRef} className="mt-6 scroll-mt-28">
            {resultsLoading ? (
              viewMode !== 'map' ? (
                <div className={viewMode === 'cards' ? 'mx-auto max-w-5xl' : ''}>
                  <p className="sr-only">{t('search.results.loading')}</p>
                  {viewMode === 'cards' ? <CardsSkeleton /> : <ResultsSkeleton />}
                </div>
              ) : (
                <div
                  className="h-[480px] animate-pulse rounded-[16px] bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none sm:h-[720px]"
                  aria-hidden="true"
                />
              )
            ) : viewMode !== 'map' ? (
              // Card view reads best at about the width it had beside the old sidebar.
              <div className={viewMode === 'cards' ? 'mx-auto max-w-5xl' : ''}>
                {/* The area the list is limited to: a drawn region, a district or a
                    city, framed on a small map. */}
                {miniMapPreview && (
                  <div className="mb-6 overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface)]">
                    <div className="h-40 w-full sm:h-48">
                      <RegionMiniMap
                        region={miniMapPreview.region}
                        frameBounds={miniMapPreview.frameBounds}
                        properties={miniMapPreview.properties}
                      />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                      <span className="flex items-center gap-2 text-[0.9375rem] font-semibold text-[var(--pv-color-text)]">
                        <i className="ri-shape-2-line text-[1.125rem] leading-none text-[var(--pv-color-primary)]" aria-hidden="true" />
                        {miniMapPreview.kind === 'drawn'
                          ? t('search.results.inDrawnArea', { what: countText })
                          : t('search.results.inPlace', { what: countText, place: miniMapPreview.name })}
                      </span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setShowMap(true)} className={OUTLINE_PILL}>
                          <i className="ri-map-2-line text-base leading-none" aria-hidden="true" />
                          {t('search.results.viewOnMap')}
                        </button>
                        {miniMapPreview.kind === 'drawn' && (
                          <button type="button" onClick={() => setDrawnRegion(null)} className={OUTLINE_PILL}>
                            <i className="ri-close-line text-base leading-none" aria-hidden="true" />
                            {t('search.results.clearArea')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {gridProperties.length > 0 && viewMode === 'cards' && (
                  <div className="flex flex-col gap-4 sm:gap-6">
                    {gridProperties.map((property, index) => (
                      <PropertyCard
                        key={property.id}
                        property={property}
                        motionOrder={index}
                        onClick={() => handlePropertySelect(property)}
                        landmarkName={filters.landmark?.name}
                        eagerImage={index < 2}
                      />
                    ))}
                  </div>
                )}
                {gridProperties.length > 0 && viewMode === 'grid' && (
                  <ul role="list" className={RESULTS_GRID}>
                    {gridProperties.map((property, index) => (
                      <li key={property.id}>
                        <ResultCard
                          property={property}
                          onSelect={handlePropertySelect}
                          landmarkName={filters.landmark?.name}
                          showListingType={filters.listingType === 'all'}
                          eagerImage={index < 4}
                        />
                      </li>
                    ))}
                  </ul>
                )}

                {/* A region is drawn but no matching property falls inside it. */}
                {drawnRegion && mapFilteredProperties.length > 0 && (regionResults?.length ?? 0) === 0 && (
                  <div className="rounded-[16px] border border-dashed border-[var(--pv-border-strong)] px-5 py-10 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                    {lang === 'ar'
                      ? 'لا توجد عقارات مطابقة لفلاترك داخل المنطقة المرسومة. جرّب رسم منطقة أخرى أو عدّل الفلاتر.'
                      : 'No properties match your filters inside your drawn area. Try drawing another area or adjusting your filters.'}
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <button type="button" onClick={() => setShowMap(true)} className={OUTLINE_PILL}>
                        <i className="ri-pencil-ruler-2-line text-base leading-none" aria-hidden="true" />
                        {lang === 'ar' ? 'ارسم منطقة أخرى' : 'Draw another area'}
                      </button>
                      <button type="button" onClick={() => setDrawnRegion(null)} className={OUTLINE_PILL}>
                        <i className="ri-close-line text-base leading-none" aria-hidden="true" />
                        {t('search.results.clearArea')}
                      </button>
                    </div>
                  </div>
                )}

                {/* 20 per page: server-driven, or client-side over the in-region set. */}
                {effectiveTotalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={effectiveTotalPages}
                    onPageChange={handlePageChange}
                    className="mt-10"
                  />
                )}
              </div>
            ) : (
              <div className="h-[480px] overflow-hidden rounded-[16px] border border-[var(--pv-border-soft)] sm:h-[720px]">
                <MapView
                  properties={mapFilteredProperties}
                  selectedProperty={selectedProperty}
                  onPropertySelect={handlePropertySelect}
                  region={drawnRegion}
                  onRegionChange={setDrawnRegion}
                  autoDraw={autoDrawPolygon}
                  onAutoDrawConsumed={() => setAutoDrawPolygon(false)}
                  selectedAreaBoundary={selectedAreaBoundary}
                  selectedAreaInsights={selectedAreaInsights}
                  selectedAreaLoading={selectedAreaLoading}
                  onClearSelectedArea={() => handleFilterChange({ areaId: null })}
                />
              </div>
            )}

            {!isLoading && !mapLoading && (drawnRegion ? mapFilteredProperties.length === 0 : filteredAndSortedProperties.length === 0) && (
              <div className="mx-auto max-w-xl px-2 py-12 text-center">
                <i
                  className={`${ragError ? 'ri-wifi-off-line' : 'ri-search-line'} text-[2.25rem] leading-none text-[var(--pv-color-text-muted)]`}
                  aria-hidden="true"
                />
                <h2 className="mt-4 text-[1.25rem] font-semibold text-[var(--pv-color-text)]">
                  {ragError
                    ? (lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Could not reach the server')
                    : t('search.results.emptyTitle')}
                </h2>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">
                  {ragError
                    ? (lang === 'ar' ? 'تحقّق من اتصالك ثم أعد المحاولة.' : 'Check your connection, then try again.')
                    : query
                      ? (lang === 'ar' ? 'لم يتم العثور على عقارات تطابق بحثك.' : 'No properties found matching your search.')
                      : t('search.results.emptyDescription')}
                </p>

                {!ragError && searchSuggestions?.properties && searchSuggestions.properties.length > 0 && (
                  <div className="mt-6">
                    <p className="text-[0.8125rem] font-medium text-[var(--pv-color-text-muted)]">
                      {lang === 'ar' ? 'عقارات مقترحة' : 'Suggested properties'}
                    </p>
                    <div className="mt-2 flex flex-wrap justify-center gap-2">
                      {searchSuggestions.properties.map((prop) => (
                        <Link key={prop.slug} to={`/property/${prop.slug}`} className={OUTLINE_PILL}>
                          {prop.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {!ragError && searchSuggestions?.categories && searchSuggestions.categories.length > 0 && (
                  <div className="mt-5">
                    <p className="text-[0.8125rem] font-medium text-[var(--pv-color-text-muted)]">
                      {lang === 'ar' ? 'تصفح حسب النوع' : 'Browse by type'}
                    </p>
                    <div className="mt-2 flex flex-wrap justify-center gap-2">
                      {searchSuggestions.categories.map((cat) => (
                        <Link key={cat.type} to={`/search?propertyType=${encodeURIComponent(cat.type)}`} className={OUTLINE_PILL}>
                          {cat.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={ragError ? retrySearch : clearAllFilters}
                  className="mt-7 inline-flex h-11 items-center gap-2 rounded-full bg-[var(--pv-color-primary)] px-6 text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
                >
                  {ragError && <i className="ri-refresh-line text-base leading-none" aria-hidden="true" />}
                  {ragError ? (lang === 'ar' ? 'إعادة المحاولة' : 'Retry') : t('search.results.emptyReset')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* «كل الفلاتر»: every filter (with sort first), as a bottom sheet on phones
            and a side panel from lg. `invisible` takes the closed drawer out of the
            tab order and the accessibility tree once it has faded. */}
        <div
          className={[
            // Opening shows it at once (so focus can move in); closing keeps it
            // visible until the fade ends.
            'fixed inset-0 z-[80] duration-300',
            filtersExpanded ? 'visible opacity-100 transition-opacity' : 'invisible opacity-0 transition-[opacity,visibility]',
          ].join(' ')}
          role="dialog"
          aria-modal="true"
          aria-labelledby="search-filters-sheet-title"
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersExpanded(false)} />

          <div
            className={[
              'absolute flex flex-col bg-[var(--pv-color-surface)] shadow-brand-md transition-transform duration-300 ease-out motion-reduce:transition-none',
              'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-[20px]',
              'lg:inset-x-auto lg:inset-y-0 lg:end-0 lg:max-h-none lg:w-[28rem] lg:rounded-none',
              filtersExpanded
                ? 'translate-y-0 lg:translate-x-0'
                : 'translate-y-full lg:translate-x-full lg:translate-y-0 lg:rtl:-translate-x-full',
            ].join(' ')}
          >
            <div className="flex justify-center pt-2.5 lg:hidden" aria-hidden="true">
              <span className="block h-1 w-10 rounded-full bg-[var(--pv-border-strong)]" />
            </div>

            <div className="flex items-center justify-between border-b border-[var(--pv-border-subtle)] px-5 py-3.5">
              <h2 id="search-filters-sheet-title" className="text-[1.0625rem] font-semibold text-[var(--pv-color-text)]">
                {t('search.filters.title')}
              </h2>
              <button
                ref={drawerCloseRef}
                type="button"
                onClick={() => setFiltersExpanded(false)}
                className="grid h-10 w-10 place-items-center rounded-full text-[var(--pv-color-text-muted)] transition-colors hover:bg-[var(--pv-color-surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30"
                aria-label={lang === 'ar' ? 'إغلاق' : 'Close'}
              >
                <i className="ri-close-line text-[1.375rem] leading-none" aria-hidden="true" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5">
              <PropertyFilters
                filters={filters}
                onFiltersChange={handleFilterChange}
                onClearAll={clearAllFilters}
                resultCount={totalCount}
                idPrefix="filters-sheet"
                layout="sheet"
                sort={sortControl}
              />
            </div>

            <div
              className="flex items-center gap-3 border-t border-[var(--pv-border-subtle)] px-5 py-3"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
              <button
                type="button"
                onClick={clearAllFilters}
                className="h-12 flex-1 rounded-full border border-[var(--pv-border-strong)] text-[0.9375rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)]"
              >
                {t('search.filters.clear')}
              </button>
              {/* The total match count (not just this page's 20 cards). */}
              <button
                type="button"
                onClick={() => setFiltersExpanded(false)}
                className="h-12 flex-[1.4] rounded-full bg-[var(--pv-color-primary)] px-4 text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
              >
                {t('search.results.show', { what: countText })}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Phones: one floating bar for the filters and the list/map switch, always
          within reach while scrolling (the wasalt.sa pattern). */}
      <div
        className="pointer-events-none fixed inset-x-0 z-[70] flex justify-center sm:hidden"
        style={{ bottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
      >
        <div className="pointer-events-auto flex items-center rounded-full bg-[var(--pv-color-primary)] text-[0.875rem] font-semibold text-white shadow-brand-md dark:text-[var(--pv-color-surface)]">
          <button type="button" onClick={() => setFiltersExpanded(true)} className="flex h-12 items-center gap-2 pe-4 ps-5">
            <i className="ri-equalizer-line text-[1.125rem] leading-none" aria-hidden="true" />
            {t('search.filters.title')}
            {activeFilterCount > 0 && (
              <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-white px-1 text-[0.6875rem] font-bold tabular-nums text-[var(--pv-color-primary)]">
                {activeFilterCount}
              </span>
            )}
          </button>
          <span className="h-6 w-px bg-current opacity-30" aria-hidden="true" />
          <button
            type="button"
            onClick={() => selectView(viewMode === 'map' ? listView : 'map')}
            className="flex h-12 items-center gap-2 pe-5 ps-4"
          >
            <i
              className={`${viewMode === 'map' ? (listView === 'cards' ? 'ri-layout-row-line' : 'ri-layout-grid-line') : 'ri-map-2-line'} text-[1.125rem] leading-none`}
              aria-hidden="true"
            />
            {viewMode === 'map' ? t('search.results.viewModes.list') : t('search.results.viewModes.map')}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}
